import mapToAlphanumericNumbers from "../helpers/mapToAlphanumericNumbers";

/**
 * Pattern to match formatted CNPJ alphanumeric (AA.AAA.AAA/AAAA-99) or 14 characters.
 */
export const CNPJ_ALPHANUMERIC_PATTERN =
  /^([0-9A-Za-z]{12}\d{2}|[0-9A-Za-z]{2}\.[0-9A-Za-z]{3}\.[0-9A-Za-z]{3}\/[0-9A-Za-z]{4}-\d{2})$/;

const CNPJ_ALPHANUMERIC_MASK = /[./-]/g;
const CNPJ_ALPHANUMERIC_INVALID_CHARS = /[^A-Z\d./-]/i;
const CNPJ_ALPHANUMERIC_NO_DV_PATTERN = /^([A-Z\d]){12}$/;
const CNPJ_ALPHANUMERIC_PATTERN_NO_MASK = /^([A-Z\d]){12}(\d){2}$/;
const CNPJ_ALPHANUMERIC_ZERO = "00000000000000";
const CNPJ_ALPHANUMERIC_LENGTH = 12;
const CNPJ_ALPHANUMERIC_WEIGHTS = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

/**
 * Check if value is a valid alphanumeric CNPJ.
 * @example ```js
 * isCNPJAlphanumeric('AB12CD34EF5602')
 * //=> true
 *
 * isCNPJAlphanumeric('AB.12C.D34/EF56-02')
 * //=> true
 *
 * isCNPJAlphanumeric('AB12CD34EF5600')
 * //=> false
 * ```
 * @param value - A text containing an alphanumeric CNPJ.
 */
const isCNPJAlphanumeric = (value: string): boolean => {
  if (!CNPJ_ALPHANUMERIC_PATTERN.test(value)) return false;
  if (CNPJ_ALPHANUMERIC_INVALID_CHARS.test(value)) return false;
  const sanitized = value.replace(CNPJ_ALPHANUMERIC_MASK, "").toUpperCase();
  if (!CNPJ_ALPHANUMERIC_PATTERN_NO_MASK.test(sanitized)) return false;
  if (sanitized === CNPJ_ALPHANUMERIC_ZERO) return false;
  const cnpjWithoutDv = sanitized.substring(0, CNPJ_ALPHANUMERIC_LENGTH);
  if (!CNPJ_ALPHANUMERIC_NO_DV_PATTERN.test(cnpjWithoutDv)) return false;
  if (
    cnpjWithoutDv ===
    CNPJ_ALPHANUMERIC_ZERO.substring(0, CNPJ_ALPHANUMERIC_LENGTH)
  )
    return false;
  const numbers = mapToAlphanumericNumbers(cnpjWithoutDv);
  let sumFirst = 0;
  let sumSecond = 0;
  for (let index = 0; index < CNPJ_ALPHANUMERIC_LENGTH; index += 1) {
    sumFirst +=
      (numbers[index] ?? 0) * (CNPJ_ALPHANUMERIC_WEIGHTS[index + 1] ?? 0);
    sumSecond +=
      (numbers[index] ?? 0) * (CNPJ_ALPHANUMERIC_WEIGHTS[index] ?? 0);
  }
  const digitFirst = sumFirst % 11 < 2 ? 0 : 11 - (sumFirst % 11);
  sumSecond +=
    digitFirst * (CNPJ_ALPHANUMERIC_WEIGHTS[CNPJ_ALPHANUMERIC_LENGTH] ?? 0);
  const digitSecond = sumSecond % 11 < 2 ? 0 : 11 - (sumSecond % 11);
  const dvInformed = sanitized.substring(CNPJ_ALPHANUMERIC_LENGTH);
  return dvInformed === `${digitFirst}${digitSecond}`;
};

export default isCNPJAlphanumeric;
